import { makeStyles } from "tss-react/mui"
import { Box, Typography } from "@mui/material"
import { multiply } from "dinero.js"

import { LocalizedStrict } from "../../../../../../modules/localization/components/AppLocalized"
import { toDinero } from "../../../../../../modules/currency/currencyLib"
import { useMoney } from "../../../../../../modules/currency/useMoney"

import type { TakeawayCartItem } from "../takeawayTypes"

const useStyles = makeStyles()((theme) => ({
    itemRow: {
        display: "grid",
        gridTemplateColumns: "1fr 5rem 5rem",
        columnGap: "1.5rem",
        alignItems: "start",
    },
    itemNameCell: {
        display: "flex",
        flexDirection: "column",
        gap: ".25rem",
    },
    itemNameText: {
        color: theme.palette.tailwind.slate[950],
    },
    payrollBadge: {
        display: "inline-block",
        color: theme.palette.tailwind.amber[900],
        background: theme.palette.tailwind.amber[200],
        fontWeight: 400,
        borderRadius: ".375rem",
        padding: "0 .375rem",
        whiteSpace: "nowrap",
        alignSelf: "flex-start",
    },
    itemQty: {
        color: theme.palette.tailwind.slate[600],
        whiteSpace: "nowrap",
    },
    itemAmount: {
        fontWeight: 700,
        color: theme.palette.tailwind.slate[950],
        textAlign: "right",
        whiteSpace: "nowrap",
    },
}))

type TakeawayCheckoutItemRowProps = Readonly<{
    item: TakeawayCartItem
    moneyFactory: ReturnType<typeof useMoney>
}>

export function TakeawayCheckoutItemRow({ item, moneyFactory }: TakeawayCheckoutItemRowProps) {
    const { classes } = useStyles()

    if (!item.product.price) return null

    const unitPrice = toDinero(item.product.price)
    const lineTotal = multiply(unitPrice, item.quantity)
    const qtyLabel = `${item.quantity} x ${moneyFactory.formatAsNumber(unitPrice)}`
    const paymentMethod = item.product.paymentDetails?.method

    return (
        <Box className={classes.itemRow}>
            <Box className={classes.itemNameCell}>
                <Typography variant="body1" className={classes.itemNameText}>
                    {item.product.name}
                </Typography>
                {paymentMethod && (
                    <LocalizedStrict
                        id="takeaway-checkout-payment-hint-by-method"
                        vars={{ method: paymentMethod.toLowerCase().replace(/_/g, " ") }}
                    >
                        <Typography variant="subtitle1" component="span" className={classes.payrollBadge}>
                            Paid by {paymentMethod}
                        </Typography>
                    </LocalizedStrict>
                )}
            </Box>
            <Typography variant="body1" className={classes.itemQty}>
                {qtyLabel}
            </Typography>
            <Typography variant="body1" className={classes.itemAmount}>
                {moneyFactory.formatAsNumber(lineTotal)}
            </Typography>
        </Box>
    )
}
